import { useEffect, useRef, useState } from "react";
import { Play, Pause, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { fmtDur } from "@/components/shally/ui";

const SPEEDS = ["0.75", "1", "1.25", "1.5", "2"];

export function AudioPlayer({ cid, canDownload }) {
  const [url, setUrl] = useState(null), [err, setErr] = useState(""), [rate, setRate] = useState("1");
  const [playing, setPlaying] = useState(false), [t, setT] = useState(0), [dur, setDur] = useState(0);
  const el = useRef(null);
  useEffect(() => {
    let u, alive = true;
    api.get(`/call-intel/calls/${cid}/audio-link`)
      .then(async (r) => {
        if (r.data.url) { if (alive) { setUrl(r.data.url); } return; }
        const b = await api.get(`/call-intel/calls/${cid}/audio`, { responseType: "blob" });
        u = URL.createObjectURL(b.data);
        if (alive) { setUrl(u); }
      })
      .catch((e) => setErr(e?.response?.status === 404 ? "The recording file is no longer available." : e?.response?.status === 403 ? "You don't have permission to play recordings." : "The recording couldn't be loaded. Please try again."));
    return () => { alive = false; if (u) { URL.revokeObjectURL(u); } };
  }, [cid]);
  useEffect(() => { if (el.current) { el.current.playbackRate = Number(rate); } }, [rate, url]);
  const toggle = () => { const a = el.current; if (!a) return; if (a.paused) { a.play(); } else { a.pause(); } };
  const download = async () => {
    try {
      const l = await api.get(`/call-intel/calls/${cid}/audio-link?download=true`);
      const a = document.createElement("a");
      if (l.data.url) { a.href = l.data.url; } else {
        const r = await api.get(`/call-intel/calls/${cid}/audio?download=true`, { responseType: "blob" });
        a.href = URL.createObjectURL(r.data);
      }
      a.download = `shally-call-${cid}.flac`; a.click();
    } catch (e) { toast.error(apiErr(e)); }
  };
  if (err) return <p className="text-sm text-muted-foreground" data-testid="ci-player-error">{err}</p>;
  if (!url) return <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="ci-player-loading"><Loader2 className="h-4 w-4 animate-spin" />Loading recording…</p>;
  return (
    <div className="rounded-xl border border-border bg-muted/40 p-3" data-testid="ci-player">
      <audio data-testid="ci-audio" src={url} ref={el} preload="metadata" className="hidden"
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => setT(e.currentTarget.currentTime)} onLoadedMetadata={(e) => setDur(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)} />
      <div className="flex items-center gap-3">
        <button type="button" onClick={toggle} aria-label={playing ? "Pause" : "Play"} data-testid="ci-play"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-transform hover:bg-primary/90 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
          {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <input type="range" min={0} max={dur || 0} step={0.1} value={Math.min(t, dur || 0)} aria-label="Seek" data-testid="ci-progress"
            onChange={(e) => { if (el.current) { el.current.currentTime = Number(e.target.value); } }}
            className="h-1.5 w-full cursor-pointer accent-[hsl(var(--primary))]" />
          <div className="mt-1 flex justify-between text-[11px] tabular-nums text-muted-foreground"><span>{fmtDur(t)}</span><span>{fmtDur(dur)}</span></div>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex rounded-lg border border-border bg-card p-0.5" role="group" aria-label="Playback speed" data-testid="ci-speed">
          {SPEEDS.map((s) => (
            <button key={s} type="button" onClick={() => setRate(s)} aria-pressed={rate === s} data-testid={`ci-speed-${s}`}
              className={`rounded-md px-2 py-1 text-xs font-semibold transition-colors ${rate === s ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"}`}>{s}×</button>))}
        </div>
        {canDownload && <button type="button" onClick={download} data-testid="ci-download"
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-card hover:text-foreground"><Download className="h-3.5 w-3.5" />Download</button>}
      </div>
    </div>);
}
